import { useEffect, useMemo, useState } from 'react';
import type { PaletteMode } from '@mui/material/styles';
import { useAuthStore } from '../stores/auth.store';

export type ThemeChoice = 'system' | 'light' | 'dark' | 'night';

export const NIGHT_FROM = 20;
export const NIGHT_UNTIL = 6;

export const isNightAt = (d: Date) => {
  const h = d.getHours();
  return h >= NIGHT_FROM || h < NIGHT_UNTIL;
};

/** Keyed by user: an unkeyed cache follows the browser to whoever signs in next. */
const CACHE_KEY = 'theme-choice';

const isChoice = (v: unknown): v is ThemeChoice =>
  v === 'light' || v === 'dark' || v === 'night' || v === 'system';

export function readCachedChoice(userId?: string): ThemeChoice {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return 'system';
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId) return 'system';
    return isChoice(parsed.choice) ? parsed.choice : 'system';
  } catch {
    return 'system';
  }
}

export function useThemeMode(): { mode: PaletteMode; choice: ThemeChoice } {
  const saved = useAuthStore((s) => s.user?.settings?.theme_mode);
  const userId = useAuthStore((s) => s.user?.id);
  const choice: ThemeChoice = saved ?? readCachedChoice(userId);

  useEffect(() => {
    try {
      if (saved && userId) localStorage.setItem(CACHE_KEY, JSON.stringify({ userId, choice: saved }));
    } catch { /* ignore */ }
  }, [saved, userId]);

  const [systemDark, setSystemDark] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const [night, setNight] = useState(() => isNightAt(new Date()));
  useEffect(() => {
    if (choice !== 'night') return;
    const id = setInterval(() => setNight(isNightAt(new Date())), 60_000);
    setNight(isNightAt(new Date()));
    return () => clearInterval(id);
  }, [choice]);

  const mode: PaletteMode = useMemo(() => {
    if (choice === 'light') return 'light';
    if (choice === 'dark') return 'dark';
    if (choice === 'night') return night ? 'dark' : 'light';
    return systemDark ? 'dark' : 'light';
  }, [choice, night, systemDark]);

  return { mode, choice };
}
